
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { generateProfileType } from '@/utils/profileNameGenerator';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words?: any;
  completed_at?: string;
  created_at?: string;
  assessment_variant?: string | null;
}

interface SearchablePersonSelectProps {
  submissions: SurveySubmission[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  label: string;
  dotColor: string;
  disabled?: boolean;
}

const SearchablePersonSelect: React.FC<SearchablePersonSelectProps> = ({
  submissions,
  value,
  onValueChange,
  placeholder,
  label,
  dotColor,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);

  const selectedPerson = submissions.find(s => s.id === value);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return '';
    }
  };

  return (
    <div className={label ? "space-y-3" : ""}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto py-2"
            disabled={disabled}
          >
            {selectedPerson ? (
              <div className="flex flex-col text-left gap-0.5 min-w-0">
                <span className="font-medium text-sm truncate">{selectedPerson.full_name}</span>
                {selectedPerson.scores && (
                  <span className="text-xs text-muted-foreground truncate">{generateProfileType(selectedPerson)}</span>
                )}
                <span className="text-xs text-muted-foreground truncate">
                  {formatDate(selectedPerson.created_at)} • {selectedPerson.email}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 bg-background" style={{ width: 'var(--radix-popover-trigger-width)' }}>
          <Command>
            <CommandInput placeholder="Search people..." />
            <CommandEmpty>No people found.</CommandEmpty>
            <CommandList className="max-h-64">
              {submissions.map((submission) => (
                <CommandItem
                  key={submission.id}
                  value={`${submission.full_name} ${submission.email} ${submission.profile_name || ''}`}
                  onSelect={() => {
                    onValueChange(submission.id);
                    setOpen(false);
                  }}
                  className="py-2 data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === submission.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-medium text-sm">{submission.full_name}</span>
                    {submission.scores && (
                      <span className="text-xs text-muted-foreground">{generateProfileType(submission)}</span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatDate(submission.created_at)} • {submission.email}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SearchablePersonSelect;
